// Magic-quadrant trend matrix: Readiness (Y) × Audience-skew or Acceleration (X).
//
// Lifted from H3-BFSI Lens vendor/dashboard.js (renderTrendMatrix). Same
// quadrant semantics:
//   Hot · rising         (high vel, high X)     — gaining steam
//   Cooling stars        (high vel, low X)      — was hot, cooling
//   Emerging             (low vel, high X)      — early signal, watch
//   Quiet · stalled      (low vel, low X)       — minimal activity
//
// In the trend tracker, "velocity" = avg_readiness_v1 (the rolled-up V1 score
// from topic_readiness). The X axis is audience-skew = the spread of the
// topic's relevance across business lines: topics tightly relevant to a few
// lines lean right; broadly relevant topics lean left. This is a snapshot
// projection until we have multi-week history; once we do, X switches to
// week-over-week acceleration like the Lens does.

import * as d3 from "d3";
import type { TopicWithContext } from "../lib/types.ts";

export interface MatrixCallbacks {
  onTopicClick: (topicId: number) => void;
}

interface Datum {
  topic_id: number;
  name: string;
  readiness: number;       // Y: avg V1 readiness 0..3
  skew: number;            // X: audience-skew rank −1..+1
  count: number;           // bubble size: number of metrics scored
  estimated_pct: number;   // % of metrics that are estimated (for opacity)
  h3: boolean;             // ring around bubble if h3_thesis_flag
  xTarget?: number;
  yTarget?: number;
  x?: number;
  y?: number;
}

const QUAD_LABELS = {
  tr: "Hot · rising",
  tl: "Cooling stars",
  br: "Emerging",
  bl: "Quiet · stalled",
};

export function renderTrendMatrix(
  host: HTMLElement,
  topics: TopicWithContext[],
  cb: MatrixCallbacks,
): () => void {
  host.innerHTML = "";
  if (!topics.length) {
    host.innerHTML = `<div class="viz-empty">No topics to plot.</div>`;
    return () => { host.innerHTML = ""; };
  }

  // Compute audience-skew per topic = how concentrated relevance is across
  // business lines. Use coefficient of variation (stddev / mean) over the
  // 11 relevance scores; topics with high concentration get high skew.
  const data: Datum[] = topics.map((t) => {
    // Coerce Score | null → number explicitly so TS doesn't insist on the
    // narrow Score | 0 union for reduce's accumulator.
    const rels: number[] = Object.values(t.relevance).map((r) => (r.score ?? 0) as number);
    const mean = rels.length ? rels.reduce((a, b) => a + b, 0) / rels.length : 0;
    const variance = rels.length
      ? rels.reduce((a, b) => a + (b - mean) ** 2, 0) / rels.length
      : 0;
    const cv = mean > 0 ? Math.sqrt(variance) / mean : 0;
    const scoreCount = Object.keys(t.scores).length;
    const estCount = Object.values(t.scores).filter((s) => s.estimated).length;
    return {
      topic_id: t.topic.id,
      name: t.topic.name,
      readiness: t.readiness?.avg_readiness_v1 ?? 0,
      skew: cv,        // raw; rank-normalized below
      count: scoreCount,
      estimated_pct: scoreCount ? estCount / scoreCount : 0,
      h3: t.topic.h3_thesis_flag,
    };
  });

  // Rank-normalize skew so the X axis is always evenly spread, even when
  // the underlying distribution clusters.
  const sorted = [...data].sort((a, b) => a.skew - b.skew);
  const N = sorted.length;
  sorted.forEach((d, i) => {
    d.skew = N === 1 ? 0 : (2 * i / (N - 1) - 1);
  });

  const W = host.clientWidth || 900;
  const H = host.clientHeight || 600;
  const margin = { top: 50, right: 30, bottom: 56, left: 78 };
  const innerW = W - margin.left - margin.right;
  const innerH = H - margin.top - margin.bottom;

  // Banner explaining the axes.
  const banner = document.createElement("div");
  banner.className = "matrix-mode-banner";
  banner.innerHTML =
    `<strong>Snapshot mode.</strong> Y = avg V1 readiness. ` +
    `X = audience-skew (rank-normalized): tightly relevant topics on the right, broadly relevant on the left. ` +
    `${data.length} topic${data.length === 1 ? "" : "s"} plotted. Click any bubble for the rationale.`;
  host.appendChild(banner);

  const svg = d3.select(host).append("svg")
    .attr("class", "matrix-svg")
    .attr("viewBox", `0 0 ${W} ${H}`);
  const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

  const x = d3.scaleLinear().domain([-1.15, 1.15]).range([0, innerW]);
  const yMax = Math.max(0.5, d3.max(data, (d) => d.readiness) ?? 0.5);
  const y = d3.scaleLinear().domain([0, yMax * 1.12]).range([innerH, 0]);
  const r = d3.scaleSqrt().domain([1, Math.max(2, d3.max(data, (d) => d.count) ?? 2)]).range([14, 38]);

  // Quadrant tints
  const x0 = x(0);
  const yMid = y(yMax * 0.5);
  g.append("rect").attr("x", x0).attr("y", 0).attr("width", innerW - x0).attr("height", yMid).attr("fill", "rgba(126,194,95,.05)");
  g.append("rect").attr("x", 0).attr("y", 0).attr("width", x0).attr("height", yMid).attr("fill", "rgba(245,136,186,.04)");
  g.append("rect").attr("x", x0).attr("y", yMid).attr("width", innerW - x0).attr("height", innerH - yMid).attr("fill", "rgba(107,168,255,.04)");
  g.append("rect").attr("x", 0).attr("y", yMid).attr("width", x0).attr("height", innerH - yMid).attr("fill", "rgba(255,255,255,.015)");

  // Quadrant labels
  g.append("text").attr("class", "matrix-quadrant-label").attr("x", innerW - 10).attr("y", 18).attr("text-anchor", "end").text(QUAD_LABELS.tr);
  g.append("text").attr("class", "matrix-quadrant-label").attr("x", 10).attr("y", 18).text(QUAD_LABELS.tl);
  g.append("text").attr("class", "matrix-quadrant-label").attr("x", innerW - 10).attr("y", innerH - 10).attr("text-anchor", "end").text(QUAD_LABELS.br);
  g.append("text").attr("class", "matrix-quadrant-label").attr("x", 10).attr("y", innerH - 10).text(QUAD_LABELS.bl);

  // Crosshairs
  g.append("line").attr("class", "matrix-zero-line").attr("x1", x(0)).attr("x2", x(0)).attr("y1", 0).attr("y2", innerH);
  g.append("line").attr("class", "matrix-zero-line").attr("x1", 0).attr("x2", innerW).attr("y1", y(yMax * 0.5)).attr("y2", y(yMax * 0.5));

  // Axis labels
  g.append("text").attr("class", "matrix-axis-label").attr("x", innerW / 2).attr("y", innerH + 42).attr("text-anchor", "middle")
    .text("← broadly relevant · audience-skew rank · tightly relevant →");
  g.append("text").attr("class", "matrix-axis-label").attr("transform", `translate(-54,${innerH/2}) rotate(-90)`).attr("text-anchor", "middle")
    .text("readiness (avg V1)");

  // Seed targets
  for (const d of data) {
    d.xTarget = x(d.skew);
    d.yTarget = y(d.readiness);
    d.x = d.xTarget;
    d.y = d.yTarget;
  }

  // Bubbles
  const bubbleG = g.append("g").attr("class", "matrix-bubbles");
  const node = bubbleG.selectAll<SVGGElement, Datum>("g.matrix-node")
    .data(data, (d) => d.topic_id).join("g")
    .attr("class", "matrix-node")
    .style("cursor", "pointer")
    .on("click", (_e, d) => cb.onTopicClick(d.topic_id));

  node.append("circle")
    .attr("class", "matrix-bubble")
    .attr("r", (d) => r(d.count))
    .attr("fill", "#5fc6c6")
    .attr("opacity", (d) => 0.85 - d.estimated_pct * 0.4) // estimated-heavy → fainter
    .attr("stroke", (d) => d.h3 ? "#ffd966" : "none")
    .attr("stroke-width", (d) => d.h3 ? 2 : 0);

  node.append("title").text((d) =>
    `${d.name}\nreadiness ${d.readiness.toFixed(2)} · skew ${d.skew.toFixed(2)} · ${d.count} metrics scored\n${Math.round(d.estimated_pct * 100)}% estimated · ${d.h3 ? "H3 thesis topic" : ""}`,
  );

  node.append("text")
    .attr("class", "matrix-bubble-label")
    .attr("dy", 4)
    .text((d) => d.name.length > 20 ? d.name.slice(0, 19) + "…" : d.name);

  // Collision-nudge sim — anchor to data target, separate overlapping bubbles.
  const sim = d3.forceSimulation<Datum>(data)
    .force("x", d3.forceX<Datum>((d) => d.xTarget!).strength(0.55))
    .force("y", d3.forceY<Datum>((d) => d.yTarget!).strength(0.55))
    .force("collide", d3.forceCollide<Datum>().radius((d) => r(d.count) + 4).strength(0.9))
    .alphaDecay(0.05)
    .on("tick", () => {
      node.attr("transform", (d) => `translate(${d.x},${d.y})`);
    });
  const stopTimer = setTimeout(() => sim.stop(), 2500);

  return () => {
    clearTimeout(stopTimer);
    sim.stop();
    host.innerHTML = "";
  };
}
